import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';

/**
 * Hook to handle real-time notification events via Socket.IO
 * Automatically invalidates React Query cache when notifications arrive
 */
export const useNotificationSocket = () => {
  const { socket, isConnected } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    // Listen for new notifications
    const handleNewNotification = () => {
      // Invalidate notifications queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    };

    // Listen for notifications marked as read
    const handleNotificationsRead = () => {
      // Invalidate queries to update UI
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    };

    // Subscribe to socket events
    socket.on('notification:new', handleNewNotification);
    socket.on('notification:read', handleNotificationsRead);

    // Cleanup listeners on unmount
    return () => {
      socket.off('notification:new', handleNewNotification);
      socket.off('notification:read', handleNotificationsRead);
    };
  }, [socket, isConnected, queryClient]);

  return { isConnected };
};
